import DatePicker from 'react-datepicker'
import { addMinutes } from 'date-fns'
import PropTypes from 'prop-types'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import SelectField from '../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../FormLegacy/FormFieldsV2/TextField'

import {
  getSurveyContinueStartTriggers,
  getSurveyStartTriggers,
  timeBasedSurvey,
  triggerBasedSurvey,
  getFrequencyMinMaxTimes,
} from './helpers'

const FrequencyInputs = ({
  values,
  setFieldValue,
  isEditingDisabled,
  handleChange,
  isAdminOrPM,
}) => {
  const { t } = useTranslation(['surveys'])
  const timezone = values.buildings[0].timezone
  const toZonedDate = (date) => (date ? toZonedTime(date, timezone) : null)
  const toUtcTimestamp = (date) => (date ? fromZonedTime(date, timezone).getTime() : null)

  switch (values.frequency) {
    case timeBasedSurvey:
      const { minTime, maxTime } = getFrequencyMinMaxTimes(values.startDate, timezone)

      return (
        <div className="flex-row">
          <div className="subtitle__wrap">
            <div className="survey-form__text">{t('surveys:frequencyInputs.startDate')}</div>
            <DatePicker
              key="startDate"
              name="startDate"
              selected={toZonedDate(values.startDate)}
              onChange={(date) => {
                setFieldValue('startDate', toUtcTimestamp(date))
                setFieldValue('startTime', null)
              }}
              placeholderText={t('surveys:form.endDatePlaceholder')}
              dateFormat="MM/dd/yyyy"
              minDate={toZonedDate(new Date())}
              maxDate={values.endDate ? toZonedDate(values.endDate) : null}
              className="survey-form__input"
              autoComplete="off"
              disabled={!isAdminOrPM || isEditingDisabled}
            />
          </div>
          <div className="subtitle__wrap">
            <div className="survey-form__text">{t('surveys:frequencyInputs.time')}</div>
            <DatePicker
              key="startTime"
              selected={values.startTime}
              onChange={(date) => {
                setFieldValue('startTime', date)
              }}
              placeholderText={t('surveys:form.timePlaceholder')}
              showTimeSelect
              showTimeSelectOnly
              timeCaption={t('surveys:frequencyInputs.timeCaption')}
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minTime}
              maxTime={maxTime}
              className="survey-form__input"
              disabled={!isAdminOrPM || !values.startDate || isEditingDisabled}
              autoComplete="off"
            />
          </div>

          <div className="subtitle__wrap">
            <div className="survey-form__text">{t('surveys:frequencyInputs.endDate')}</div>
            <DatePicker
              key="endDate"
              selected={toZonedDate(values.endDate)}
              onChange={(date) => setFieldValue('endDate', toUtcTimestamp(date))}
              placeholderText={t('surveys:form.endDatePlaceholder')}
              dateFormat="MM/dd/yyyy"
              minDate={
                values.startDate
                  ? addMinutes(toZonedDate(values.startDate), 1440)
                  : toZonedDate(new Date())
              }
              className="survey-form__input"
              autoComplete="off"
              disabled={!isAdminOrPM || isEditingDisabled}
            />
          </div>
        </div>
      )

    case triggerBasedSurvey:
      return (
        <div className="flex-row">
          <div className="subtitle__wrap">
            <div className="survey-form__text">{t('surveys:form.surveyStartTriggerLabel')}</div>
            <div className="flex-row">
              <TextField
                name="startTriggerDays"
                placeholder={t('surveys:form.noDataDaysPlaceholder')}
                type="number"
                id="startTriggerDays"
                onChange={(e) => {
                  if (e.target.value <= 0 || e.target.value > 180) return
                  setFieldValue('startTriggerDays', e.target.value)
                }}
                value={values.startTriggerDays || ''}
                className="survey-form__input"
                extraFieldClass="survey-form__field survey-form__field--short survey-form__number-of-days-field"
                disabled={isEditingDisabled}
              />
              <div className="survey-form__text">{t('surveys:form.daySuffix')}</div>
              <SelectField
                name="startTriggerTime"
                value={values.startTriggerTime}
                placeholder={t('surveys:form.selectStartTriggerPlaceholder')}
                options={getSurveyContinueStartTriggers(t)}
                onChange={handleChange}
                extraInputClass="survey-form__input"
                additionalClass="survey-form__field--medium"
                disabled={isEditingDisabled}
              />
              <SelectField
                name="startTriggerType"
                value={values.startTriggerType}
                placeholder={t('surveys:form.selectStartTriggerPlaceholder')}
                options={getSurveyStartTriggers(t)}
                onChange={handleChange}
                extraInputClass="survey-form__input"
                additionalClass="survey-form__field survey-form__field--limited-width"
                disabled={isEditingDisabled}
              />
            </div>
          </div>
        </div>
      )
    default:
      return null
  }
}

export default FrequencyInputs

FrequencyInputs.propTypes = {
  values: PropTypes.object,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  isEditingDisabled: PropTypes.bool,
  isAdminOrPM: PropTypes.bool,
}
